import { Add } from '@mui/icons-material';
import { Box, Button, List, ListItemButton, ListItemText } from '@mui/material';
import { uniq } from 'lodash';
import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useGetCompanyOffices } from '~/data/companyOffices';
import { useAllCostCenters } from '~/data/costCenters/useAllCostCenters';
import { BusinessUnit, OrganizationUnit } from '~/data/orgunits';
import {
  createOrganizationUnit,
  deleteOrganizationUnit,
  getOrgTree,
  updateOrganizationUnit,
} from '~/data/orgunits/slice';
import { useAllOrgUnits } from '~/data/orgunits/useAllOrgUnits';
import {
  getAlternateManagerIds,
  getManagerIds,
  getTeamManagerIds,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/managerIds';
import { LeftSideMenu } from '~/features/Sidebars/LeftSideMenu';
import { useVisible } from '~/hooks/useVisible';
import { useSearchData } from '~/legacy-components/ModulePageFooter/components/EmployeesSearch/useSearchData';
import {
  OrgUnitType,
  getCompanyOrgUnitTypes,
} from '~/scenes/PeopleModule/components/Tabs/BasicTab/data';
import { SidebarLayout } from '~/scenes/PortalViews/components/SidebarLayout';
import { fetchFromOldApi } from '~/services/Http';
import { isRoleDevHR, isRoleHR } from '~/services/Permissions';

import { OrgUnitPopup } from './components/OrgUnitPopup';
import { useSxStyles } from './useStyles';

export const getResponsibleHrOptionIds = async (): Promise<number[]> => {
  const fetchRoleIds = (role: string) =>
    fetchFromOldApi(`/json/user/role/${role}`).then((r) => r.json());

  return await Promise.all(
    [
      'HR_ADMIN',
      'HR_ADMIN_LIMITED',
      'HR_STAFF',
      'HR_DEV',
      'HR_SALARY',
      'HR_AUDITOR',
      'HR_PEOP',
      'HR_PEOP_DEV',
    ].map(fetchRoleIds),
  ).then((results) => uniq([...results].flat()));
};

export const PortalAltMgr = () => {
  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const sidebarState = useVisible(true);

  const loggedInUserRoles = useLoggedInUserRoles();
  const isHR = isRoleHR(loggedInUserRoles);
  const isDevHR = isRoleDevHR(loggedInUserRoles);

  const { orgUnits: allUnits } = useAllOrgUnits();
  const dispatch = useDispatch();

  const { data: offices } = useGetCompanyOffices();

  const [open, setOpen] = React.useState(false);
  const [selectedUnit, setSelectedUnit] =
    React.useState<OrganizationUnit | null>(null);

  const [unitTypes, setUnitTypes] = React.useState<OrgUnitType[]>([]);
  const [managerIds, setManagerIds] = React.useState<number[]>([]);
  const [teamManagerIds, setTeamManagerIds] = React.useState<number[]>([]);
  const [orgTrees, setOrgTrees] = React.useState<BusinessUnit[]>([]);
  const [hrResponsibleIds, setHrResponsibleIds] = React.useState<number[]>([]);
  const [alternateMgrIds, setAlternateMgrIds] = React.useState<number[]>([]);

  const { allEmployees } = useSearchData();
  const { costCenters } = useAllCostCenters();

  const handleViewUnit = async (unitId: number) => {
    try {
      const response = await fetchFromOldApi(`/json/org/unit/${unitId}`);
      const updatedUnit = await response.json();
      setSelectedUnit(updatedUnit);
      setOpen(true);
    } catch (error) {
      console.error('Failed to fetch the updated unit data:', error);
    }
  };

  const handleAddNewUnit = useCallback(() => {
    setSelectedUnit(null);
    setOpen(true);
  }, []);

  React.useEffect(() => {
    getCompanyOrgUnitTypes().then((types) => setUnitTypes(types));
    getManagerIds().then((ids) => setManagerIds(ids));
    getTeamManagerIds().then((ids) => setTeamManagerIds(ids));
    getOrgTree().then((trees) => setOrgTrees(trees));
    getAlternateManagerIds().then((ids) => setAlternateMgrIds(ids));
    getResponsibleHrOptionIds().then((ids) => setHrResponsibleIds(ids));
  }, []);

  const handleClose = () => {
    setOpen(false);
    setSelectedUnit(null);
  };

  const handleCreateUnit = (updatedUnit: OrganizationUnit) => {
    dispatch(createOrganizationUnit(updatedUnit));
  };

  const handleUpdateUnit = (updatedUnit: OrganizationUnit) => {
    dispatch(updateOrganizationUnit(updatedUnit));
  };

  const handleDeleteUnit = (deletedUnit: OrganizationUnit) => {
    dispatch(deleteOrganizationUnit(deletedUnit));
  };

  return (
    <SidebarLayout
      sidebar={
        <LeftSideMenu
          open={sidebarState.isVisible}
          handleDrawerOpen={sidebarState.show}
          handleDrawerClose={sidebarState.hide}
          routerLink={true}
        />
      }
    >
      <Box sx={sxStyles.pageContent}>
        <Button
          onClick={handleAddNewUnit}
          startIcon={<Add />}
          disabled={!(!isDevHR && isHR)}
        >
          {t('laAdd')}
        </Button>
        <List>
          {allUnits.map((unit) => (
            <ListItemButton
              key={unit.fOrgUnitId}
              sx={sxStyles.listItem}
              onClick={() => handleViewUnit(unit.fOrgUnitId)}
            >
              <ListItemText primary={unit.fOrgUnitName} />
            </ListItemButton>
          ))}
        </List>
        <OrgUnitPopup
          open={open}
          handleClose={handleClose}
          selectedUnit={selectedUnit}
          handleUpdateUnit={handleUpdateUnit}
          handleDeleteUnit={handleDeleteUnit}
          handleCreateUnit={handleCreateUnit}
          unitTypes={unitTypes}
          offices={offices}
          managerIds={managerIds}
          teamManagerIds={teamManagerIds}
          allEmployees={allEmployees}
          orgTrees={orgTrees}
          costCenters={costCenters}
          hrResponsibleIds={hrResponsibleIds}
          alternateMgrIds={alternateMgrIds}
        />
      </Box>
    </SidebarLayout>
  );
};
